import classNames from 'classnames';
import { ChangeEvent, HTMLAttributes } from 'react';
import { Controller, FieldPath, FieldValues, useFormContext } from 'react-hook-form';
import { createFormFieldStyles } from '~/constants/styles';
import { FormFeedback } from '../FormFeedback';
import { FormField } from '../FormField';
import { getFieldError } from '../formUtils';
import { FormFieldProps, RecommendableFieldProps } from '../interfaces';

interface Props<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>
  extends FormFieldProps<TFieldValues, TName>, RecommendableFieldProps {
  autoComplete?: string;
  type?: string;
  min?: number | string;
  max?: number | string;
  inputMode?: HTMLAttributes<HTMLInputElement>['inputMode'];
  /** HTML maxlength attribute — hard cap on characters the browser will accept. */
  maxLength?: number;
  onChange?: (input: ChangeEvent<HTMLInputElement>) => void;
  /** Suppress this field's inline error. Use when an outer component owns the
      error message for this input (e.g. SignatureRenderer's custom copy). */
  hideError?: boolean;
  /** [MCV-1547] Opt a `type="number"` field back into negative values. Numeric
      inputs are non-negative by default; nothing in the listing/search forms
      accepts a negative figure, so the guard is the default rather than a flag
      each of the ~50 numeric fields has to remember to set. */
  allowNegative?: boolean;
}
export const TextInput = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  props: Props<TFieldValues, TName>
) => {
  const {
    name,
    description,
    disabled,
    cyTag,
    label,
    className,
    placeholder,
    autoComplete,
    type,
    min,
    max,
    inputMode,
    maxLength,
    tooltip,
    onChange: customOnChange,
    required,
    flagged,
    hideError,
    allowNegative,
    recommended,
    errorCyTag,
    reserveFeedbackSpace,
  } = props;
  const {
    formState: { errors },
  } = useFormContext();
  const error = getFieldError(errors, name);

  // [MCV-1547] Negative figures are meaningless in every numeric field we
  // render (provider counts, shift counts, rates, contract values), and the
  // browser's spinner plus a bare `-` keystroke both walked below zero. Guard
  // at three points, because each covers a hole the others leave: `min` stops
  // the spinner, the keydown stops typing, and the change sanitiser stops
  // paste and browser autofill.
  const blockNegative = type === 'number' && !allowNegative;
  const resolvedMin = blockNegative && min === undefined ? 0 : min;
  // [MCV-1547] `min` is also what switches ON the browser's step-mismatch
  // constraint. A number input carrying neither `min` nor `step` has no allowed
  // value step at all; the moment `min` appears it establishes a step base and
  // brings the default step of 1 with it, so every decimal — a 1.5x shift
  // differential, an hourly rate, 0.5 CME credits — becomes natively invalid.
  // The failure is silent and total: the browser refuses to fire submit, so
  // react-hook-form never runs and there is no message telling the user why the
  // button did nothing.
  //
  // None of these fields want browser-enforced granularity, so `step="any"`
  // goes on every numeric input rather than only the ones we add `min` to — a
  // caller passing its own `min` (CMETrainingForm's credits) hits exactly the
  // same wall. Whole-number fields are still held to integers by their zod
  // schema (see `nonNegativeInt`), which is where a message the user can act on
  // comes from.
  const resolvedStep = type === 'number' ? 'any' : undefined;
  return (
    <Controller
      name={name}
      render={({ field }) => {
        const value = field.value || field.value === 0 ? field.value : '';
        // [MCV-1066] `value` is already normalised to '' for null/undefined
        // above, so this also treats a cleared date input as empty.
        const isEmpty = value === '';
        const recommendation = recommended && isEmpty ? recommended : undefined;
        const errorMessage = error?.message;
        const describedBy = errorMessage
          ? `${name}-error`
          : recommendation
            ? `${name}-recommendation`
            : description
              ? `${name}-helper`
              : undefined;
        return (
          <FormField
            id={name}
            label={label}
            tooltip={tooltip}
            required={required}
            feedback={
              <FormFeedback
                error={hideError ? undefined : errorMessage}
                errorId={`${name}-error`}
                errorCyTag={errorCyTag ?? (cyTag ? `${cyTag}-error` : undefined)}
                description={description}
                helperId={`${name}-helper`}
                recommendation={recommendation}
                recommendationId={`${name}-recommendation`}
                recommendationCyTag={cyTag ? `${cyTag}-recommendation` : undefined}
                reserveSpace={reserveFeedbackSpace}
              />
            }
          >
            <div data-cy={cyTag}>
              {/* [MCV-1066] A native input, not Headless UI's <Input>.
                  Headless UI derives aria-describedby from a surrounding
                  <Field>/<Description> and discards a caller-supplied value when
                  there is no Field, so the id computed below never reached the
                  DOM -- measured, and it affected the pre-existing error and
                  helper paths too, meaning no field in the app announced its
                  validation message. Wrapping in <Field> would fix it but adds a
                  div to every form row; `as={Fragment}` does not wire it up at
                  all and injects a hidden span. Nothing here used <Input>'s
                  extras (data-headlessui-state / data-focus are unreferenced
                  outside Checkbox), so the native element is the smaller change. */}
              <input
                {...field}
                id={name}
                value={value}
                autoComplete={autoComplete}
                className={classNames(createFormFieldStyles(flagged, disabled, !!error), className)}
                onKeyDown={
                  blockNegative
                    ? (e) => {
                        if (e.key === '-') e.preventDefault();
                      }
                    : undefined
                }
                onChange={(e) => {
                  // Sanitise by numeric value rather than by stripping '-'
                  // characters. A minus is not always a sign: in "1e-3" it is
                  // part of a positive exponent, and deleting it turns 0.001
                  // into 1000. Only a value that is actually negative is
                  // folded, which matches what typing gives — the keydown
                  // guard above means a typed '-' never lands either.
                  if (blockNegative) {
                    const numeric = Number(e.target.value);
                    if (e.target.value !== '' && numeric < 0) {
                      e.target.value = String(Math.abs(numeric));
                    }
                  }
                  // Custom onChange runs first so a consumer (e.g. DatePicker's
                  // 5-6 digit year clamp) can mutate e.target.value before RHF
                  // snapshots it. Reversing the order silently dropped the clamp.
                  customOnChange?.(e);
                  field.onChange(e);
                }}
                type={type}
                min={resolvedMin}
                step={resolvedStep}
                max={max}
                inputMode={inputMode}
                maxLength={maxLength}
                disabled={disabled}
                placeholder={placeholder}
                aria-invalid={!!error || undefined}
                aria-required={required || undefined}
                aria-describedby={describedBy}
                aria-errormessage={errorMessage ? `${name}-error` : undefined}
              />
            </div>
          </FormField>
        );
      }}
    />
  );
};
